import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Edit, Trash2, Copy, Star, ChevronDown, ChevronRight, ShoppingCart, Package, Eye } from 'lucide-react';
import { useListesAvitaillement, ListeAvitaillement } from '@/hooks/useListesAvitaillement';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import DetailListeAvitaillement from './DetailListeAvitaillement';
import { useTranslation } from 'react-i18next';

interface GestionListesAvitaillementProps {
  bateaux: any[];
  selectedBateauId?: string;
  onCreerListeAchat: (liste: ListeAvitaillement) => void;
  onGenererPDF: (liste: ListeAvitaillement) => void;
}

const GestionListesAvitaillement = ({ 
  bateaux, 
  selectedBateauId,
  onCreerListeAchat,
  onGenererPDF 
}: GestionListesAvitaillementProps) => {
  const { listes, loading, modifierListe, supprimerListe, dupliquerListe, toggleFavori, creerArticlesInventaire } = useListesAvitaillement();
  const { t } = useTranslation('inventory');
  const [openSections, setOpenSections] = useState<Set<string>>(new Set(['mes-listes']));
  const [editingListe, setEditingListe] = useState<ListeAvitaillement | null>(null);
  const [editNom, setEditNom] = useState('');
  const [detailListe, setDetailListe] = useState<ListeAvitaillement | null>(null);

  const toggleSection = (section: string) => {
    const newOpenSections = new Set(openSections);
    if (newOpenSections.has(section)) {
      newOpenSections.delete(section);
    } else {
      newOpenSections.add(section);
    }
    setOpenSections(newOpenSections);
  };

  const handleEdit = (liste: ListeAvitaillement) => {
    setEditingListe(liste);
    setEditNom(liste.nom);
  };

  const handleSaveEdit = async () => {
    if (!editingListe) return;
    
    await modifierListe(editingListe.id, { nom: editNom });
    setEditingListe(null);
    setEditNom('');
  };

  const handleViewDetails = (liste: ListeAvitaillement) => {
    setDetailListe(liste);
  };

  const handleSaveArticles = async (articles: any[]) => {
    if (!detailListe) return;
    await modifierListe(detailListe.id, { articles });
  };

  const handleCreerInventaire = async () => {
    if (!detailListe) return;
    await creerArticlesInventaire(detailListe.id);
  };

  const handleCreerListeAchat = () => {
    if (detailListe) {
      onCreerListeAchat(detailListe);
    }
  };

  const getBateauName = (bateauId: string | null) => {
    if (!bateauId) return t('gestionListes.allBoats');
    const bateau = bateaux.find(b => b.id === bateauId);
    return bateau ? `${bateau.nom} - ${bateau.nom_constructeur} ${bateau.nom_modele}` : t('gestionListes.unknownBoat');
  };

  // Filtrer les listes par bateau sélectionné
  const listesFiltrees = selectedBateauId 
    ? listes.filter(l => l.bateau_id === selectedBateauId)
    : listes;

  const listesFavorites = listesFiltrees.filter(l => l.est_favori);
  const listesNormales = listesFiltrees.filter(l => !l.est_favori);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Package className="h-6 w-6 animate-pulse text-nautic mr-2" />
            <span>{t('gestionListes.loading')}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Favoris */}
      {listesFavorites.length > 0 && (
        <Card>
          <Collapsible open={openSections.has('favoris')} onOpenChange={() => toggleSection('favoris')}>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors py-3">
                <CardTitle className="flex items-center justify-between text-base">
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-yellow-500" />
                    {t('gestionListes.favorites', { count: listesFavorites.length })}
                  </div>
                  {openSections.has('favoris') ? (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-500" />
                  )}
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            
            <CollapsibleContent>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {listesFavorites.map((liste) => (
                    <ListeCard
                      key={liste.id}
                      liste={liste}
                      bateauName={getBateauName(liste.bateau_id)}
                      onEdit={handleEdit}
                      onDelete={supprimerListe}
                      onDuplicate={dupliquerListe}
                      onToggleFavori={toggleFavori}
                      onViewDetails={handleViewDetails}
                      onCreerListeAchat={onCreerListeAchat}
                      onGenererPDF={onGenererPDF}
                    />
                  ))}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>
      )}

      {/* Mes listes */}
      <Card>
        <Collapsible open={openSections.has('mes-listes')} onOpenChange={() => toggleSection('mes-listes')}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors py-3">
              <CardTitle className="flex items-center justify-between text-base">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-nautic" />
                  {t('gestionListes.myLists', { count: listesNormales.length })}
                </div>
                {openSections.has('mes-listes') ? (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                )}
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <CardContent className="pt-0">
              {listesNormales.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  {t('gestionListes.noListCreated')}
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {listesNormales.map((liste) => (
                    <ListeCard
                      key={liste.id}
                      liste={liste}
                      bateauName={getBateauName(liste.bateau_id)}
                      onEdit={handleEdit}
                      onDelete={supprimerListe}
                      onDuplicate={dupliquerListe}
                      onToggleFavori={toggleFavori}
                      onViewDetails={handleViewDetails}
                      onCreerListeAchat={onCreerListeAchat}
                      onGenererPDF={onGenererPDF}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Dialog d'édition */}
      <Dialog open={editingListe !== null} onOpenChange={() => setEditingListe(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('gestionListes.editListTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="nom">{t('gestionListes.listName')}</Label>
              <Input
                id="nom"
                value={editNom}
                onChange={(e) => setEditNom(e.target.value)}
                placeholder={t('gestionListes.listNamePlaceholder')}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditingListe(null)}>
                {t('gestionListes.cancel')}
              </Button>
              <Button onClick={handleSaveEdit}>
                {t('gestionListes.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de détails */}
      {detailListe && (
        <DetailListeAvitaillement
          liste={detailListe}
          isOpen={detailListe !== null}
          onClose={() => setDetailListe(null)}
          onSave={handleSaveArticles}
          onCreerInventaire={handleCreerInventaire}
          onCreerListeAchat={handleCreerListeAchat}
        />
      )}
    </div>
  );
};

interface ListeCardProps {
  liste: ListeAvitaillement;
  bateauName: string;
  onEdit: (liste: ListeAvitaillement) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onToggleFavori: (id: string) => void;
  onViewDetails: (liste: ListeAvitaillement) => void;
  onCreerListeAchat: (liste: ListeAvitaillement) => void;
  onGenererPDF: (liste: ListeAvitaillement) => void;
}

const ListeCard = ({
  liste,
  bateauName,
  onEdit,
  onDelete,
  onDuplicate,
  onToggleFavori,
  onViewDetails,
  onCreerListeAchat,
  onGenererPDF
}: ListeCardProps) => {
  const { t } = useTranslation('inventory');
  return (
    <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm text-nautic truncate" title={liste.nom}>
            {liste.nom}
          </h4>
          <p className="text-xs text-muted-foreground truncate" title={bateauName}>
            {bateauName}
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onToggleFavori(liste.id)}
          className="p-1 h-6 w-6 flex-shrink-0"
        >
          <Star className={`h-3 w-3 ${liste.est_favori ? 'text-yellow-500 fill-current' : 'text-gray-400'}`} />
        </Button>
      </div>

      <div className="flex flex-wrap gap-1 mb-3">
        <Badge variant="outline" className="text-xs">
          {liste.articles.length} {t('gestionListes.articles')}
        </Badge>
        <Badge variant="outline" className="text-xs">
          {liste.nombre_personnes} {t('gestionListes.people')}
        </Badge>
        <Badge variant="outline" className="text-xs">
          {liste.nombre_jours} {t('gestionListes.days')}
        </Badge>
      </div>

      <div className="text-xs text-muted-foreground mb-3">
        {t('gestionListes.createdOn', { date: format(new Date(liste.created_at), "dd/MM/yy", { locale: fr }) })}
      </div>

      <div className="flex flex-wrap gap-1">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onViewDetails(liste)}
          className="h-7 px-2 text-xs bg-nautic text-white hover:bg-nautic-dark border-nautic"
        >
          <Eye className="h-3 w-3 mr-1" />
          {t('gestionListes.viewDetails')}
        </Button>
        
        <Button
          size="sm"
          variant="outline"
          onClick={() => onEdit(liste)}
          className="h-7 px-2 text-xs"
        >
          <Edit className="h-3 w-3 mr-1" />
          {t('gestionListes.edit')}
        </Button>
        
        <Button
          size="sm"
          variant="outline"
          onClick={() => onDuplicate(liste.id)}
          className="h-7 px-2 text-xs"
        >
          <Copy className="h-3 w-3 mr-1" />
          {t('gestionListes.duplicate')}
        </Button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
            >
              <Trash2 className="h-3 w-3 mr-1" />
              {t('gestionListes.delete')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('gestionListes.deleteListTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('gestionListes.deleteListDescription', { name: liste.nom })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('gestionListes.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onDelete(liste.id)}
                className="bg-red-600 hover:bg-red-700"
              >
                {t('gestionListes.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default GestionListesAvitaillement;
